
import { setDomainState } from "../../state/actions/domain";
import { setBotWaitLoader } from "../../state/actions/loader";
import { getSocketConnection } from "../../state/actions/socket";
import store from "../../state/configureStore";
import config from "../../utils/config/config";
import { uuidGenerator, getValidAccessToken } from "../../utils/general";
import { UserServiceClientApi } from "../api/UserApiServices";
import { startConversation } from "./socketEvents";
import webSocketApiService from "./wsApiServices";
import { handlePushMessage } from "./wsPushHandler";

export const SOCKET = {
  ws: null,
  pingInterval: null,
  reconnectTimeout: null,
  pendingRequests: {}, // requestId -> { resolve, reject }
  isConnecting: false,
  isConnected: false,
  shouldReconnect: true,
  reconnectCount: 0
};

const HEARTBEAT_INTERVAL = 120000; // 2 min
const RECONNECT_DELAY = 3000;

// Build correct API Gateway payload format
export function buildRequest(service, method, payload = {}) {
  return {
    requestId: uuidGenerator(),
    service,
    method,
    payload
  };
}

export function getReconnectCount() {
  return SOCKET.reconnectCount;
}

export function resetReconnectCount() {
  SOCKET.reconnectCount = 0;
  localStorage.removeItem('websocket_reconnect_count');
}

function saveReconnectCount() {
  localStorage.setItem('websocket_reconnect_count', SOCKET.reconnectCount);
}

//Initialize WebSocket connection
export async function initWebSocket(params, isBotchat = false) {
  const { userId, appType, platform } = params || {};

  if (SOCKET.isConnecting) return;
  SOCKET.isConnecting = true;
  SOCKET.shouldReconnect = true;

  try {
    // wait for config
    await new Promise(resolve => setTimeout(resolve, 300));

    const accessToken = await getValidAccessToken(params.accessToken);

    if (!accessToken) {
      console.error("Access token missing");
      store.dispatch(getSocketConnection(false));
      SOCKET.isConnecting = false;
      return;
    }

    params.accessToken = accessToken;

    // Build WS URL
    const wsUrl = `${config.websocketURL}?slt=${accessToken}&apptype=${appType}&plfm=${platform}`;
    console.log("WS URL:", wsUrl);

    const ws = new WebSocket(wsUrl);
    SOCKET.ws = ws;

    ws.onopen = async () => {
      console.info(`WS connected ${isBotchat ? "(bot)" : ""}`);

      store.dispatch(getSocketConnection(true));
      SOCKET.isConnecting = false;
      SOCKET.isConnected = true;

      const res = await webSocketApiService.GetWSConnectionId();
      if (res?.success) {
        localStorage.setItem("connectionId", res.connectionId);
      }
      sendPing();
      SOCKET.pingInterval = setInterval(sendPing, HEARTBEAT_INTERVAL);
    };

    const parentMessageHandler = (event) => {
      handleIncomingMessage(event.data);
    };

    ws.addEventListener("message", parentMessageHandler);

    ws.onerror = () => {
      store.dispatch(getSocketConnection(false));
    };

    ws.onclose = async (event) => {
      SOCKET.isConnecting = false;
      SOCKET.isConnected = false;
      clearInterval(SOCKET.pingInterval);
      if (event.code === 403) {
        console.warn("WS closed due to expired token → reconnecting");

        try {
          SOCKET.reconnectCount++;
          saveReconnectCount();
          attemptReconnect(params);
        } catch {
          console.error("Re-auth required");
        }
        return;
      }

      if (SOCKET.shouldReconnect) {
        console.warn("WS closed due to unknown reason → reconnecting", event);
        SOCKET.reconnectCount++;
        saveReconnectCount();
        attemptReconnect(params);
      }
    };

    return ws;
  } catch (error) {
    console.error("WebSocket init failed:", error);
    SOCKET.isConnecting = false;
    SOCKET.isConnected = false;
  }
}



// Send PingService → Ping
function sendPing() {
  if (!SOCKET.ws || SOCKET.ws.readyState !== WebSocket.OPEN) return;

  const ping = buildRequest("PingService", "Ping", {});
  SOCKET.ws.send(JSON.stringify(ping));

  console.info("%c WebSocket >>> Ping sent", "color:green;");
};


// Incoming message handler
export const handleIncomingMessage = async (rawData) => {
  try {
    const msg = JSON.parse(rawData);
    console.log("%c WS <<< Message:", "color:green;", msg);

    const { requestId, type, service, method } = msg;
    // 1. If this is a response to a request we sent
    // 1. Handle real pending request (ONLY if it truly exists)
    if (requestId && SOCKET.pendingRequests[requestId]) {

      const { resolve, reject } = SOCKET.pendingRequests[requestId];
      delete SOCKET.pendingRequests[requestId];
      // console.log("%c WS <<< Pending requests count after deletion:", "color:blue;", Object.keys(SOCKET.pendingRequests).length);

      if (type === 0) {
        // console.log("%c WS <<< Resolving with payload:", "color:green;", msg.payload);
        resolve(msg.payload);
      } else {
        console.log("%c WS <<< Error:", "color:red;", msg);
        reject(msg.error || "Unknown WebSocket error");
      }

      // return; // stop — do NOT pass to push handler
    }

    // 2. Handle BackendPushService (no pending request resolution)
    if (service) {
      // console.log("%c WS <<< Handling as push message:", "color:orange;", service);
      handlePushMessage(msg);
      return;
    }

    // Debug: Check if this is a response that wasn't handled
    if (requestId && !SOCKET.pendingRequests[requestId]) {
      console.log("%c WS <<< Response for unknown request:", "color:red;", requestId);
      console.log("%c WS <<< Current pending requests:", "color:red;", Object.keys(SOCKET.pendingRequests));
    }
  } catch (err) {
    console.error("Invalid WS message", err);
  }
};


//Auto reconnect logic
async function attemptReconnect(params) {
  if (!SOCKET.shouldReconnect || SOCKET.reconnectTimeout) return;

console.log("%c Reconnecting WebSocket..SOCKET.", SOCKET);
  console.log("%c Reconnecting WebSocket...", "color:orange;");
  const response = await getValidAccessToken(params.accessToken);
  console.log("Got access token...", response);
  if (response) {
    params.accessToken = response;
  }

  SOCKET.reconnectTimeout = setTimeout(() => {
    SOCKET.reconnectTimeout = null;
    initWebSocket(params);
  }, RECONNECT_DELAY);
};


//Manual disconnect
export function disconnectWebSocket() {
  SOCKET.shouldReconnect = false;
  if (SOCKET.reconnectTimeout) {
    clearTimeout(SOCKET.reconnectTimeout);
    SOCKET.reconnectTimeout = null;
  }

  if (SOCKET.ws) SOCKET.ws.close();
  console.log("WebSocket manually disconnected");
  clearInterval(SOCKET.pingInterval);
  resetReconnectCount()
  SOCKET.pendingRequests = {};
};
